import { useCallback, useEffect, useRef } from 'react';
import type { Node, Edge } from '@xyflow/react';
import { shouldSkipShortcut, isPrimaryMod } from '@/utils/input';

/**
 * Options for the canvas keyboard navigation hook.
 */
export interface CanvasKeyboardNavigationOptions {
  /** All nodes in the canvas */
  nodes: Node[];
  /** All edges in the canvas */
  edges: Edge[];
  /** Currently selected node IDs */
  selectedNodeIds: string[];
  /** Callback when a node should be selected */
  onNodeSelect: (nodeId: string, addToSelection?: boolean) => void;
  /** Callback when a node should be deleted */
  onNodeDelete?: (nodeId: string) => void;
  /** Callback when a node should be edited (opened) */
  onNodeEdit?: (nodeId: string) => void;
  /** Callback when all nodes should be deselected */
  onDeselectAll?: () => void;
  /** Callback when all nodes should be selected */
  onSelectAll?: () => void;
  /** Callback when selected nodes should be duplicated */
  onDuplicateSelected?: () => void;
  /** Whether keyboard navigation is enabled */
  enabled?: boolean;
  /** Layout orientation for determining arrow key directions */
  layoutOrientation?: 'horizontal' | 'vertical';
}

/**
 * Find nodes connected via edges to the given node.
 */
function getConnectedNodes(
  nodeId: string,
  edges: Edge[],
  direction: 'sources' | 'targets'
): string[] {
  if (direction === 'sources') {
    // Find nodes that connect TO this node (incoming edges)
    return edges.filter((e) => e.target === nodeId).map((e) => e.source);
  } else {
    // Find nodes this node connects TO (outgoing edges)
    return edges.filter((e) => e.source === nodeId).map((e) => e.target);
  }
}

/**
 * Find sibling nodes (nodes at the same depth level in the workflow).
 * Siblings share the same parent (source) nodes.
 */
function getSiblingNodes(
  nodeId: string,
  nodes: Node[],
  edges: Edge[]
): string[] {
  // Get parents of the current node
  const parents = getConnectedNodes(nodeId, edges, 'sources');

  if (parents.length === 0) {
    // No parents - this might be a root node, find other root nodes
    const nodesWithParents = new Set(edges.map((e) => e.target));
    return nodes
      .filter((n) => !nodesWithParents.has(n.id) && n.id !== nodeId)
      .map((n) => n.id);
  }

  // Get all children of the same parents (siblings)
  const siblings = new Set<string>();
  parents.forEach((parentId) => {
    const children = getConnectedNodes(parentId, edges, 'targets');
    children.forEach((childId) => {
      if (childId !== nodeId) {
        siblings.add(childId);
      }
    });
  });

  return Array.from(siblings);
}

/**
 * Sort nodes by their position for sequential navigation.
 */
function sortNodesByPosition(
  nodes: Node[],
  orientation: 'horizontal' | 'vertical'
): Node[] {
  return [...nodes].sort((a, b) => {
    if (orientation === 'horizontal') {
      // Left to right, then top to bottom
      if (Math.abs(a.position.y - b.position.y) < 50) {
        return a.position.x - b.position.x;
      }
      return a.position.y - b.position.y;
    } else {
      // Top to bottom, then left to right
      if (Math.abs(a.position.x - b.position.x) < 50) {
        return a.position.y - b.position.y;
      }
      return a.position.x - b.position.x;
    }
  });
}

/**
 * Hook that provides keyboard navigation for the workflow canvas.
 *
 * Navigation patterns:
 * - Arrow Up: Navigate to parent node (source of incoming edge)
 * - Arrow Down: Navigate to child node (target of outgoing edge)
 * - Arrow Left/Right: Navigate to sibling nodes (same depth level)
 * - Tab/Shift+Tab: Sequential navigation through all nodes
 * - Enter: Open/edit the selected node
 * - Delete/Backspace: Delete the selected node
 * - Escape: Deselect all nodes
 * - Ctrl/Cmd+A: Select all nodes
 * - Ctrl/Cmd+D: Duplicate selected nodes
 */
export function useCanvasKeyboardNavigation({
  nodes,
  edges,
  selectedNodeIds,
  onNodeSelect,
  onNodeDelete,
  onNodeEdit,
  onDeselectAll,
  onSelectAll,
  onDuplicateSelected,
  enabled = true,
  layoutOrientation = 'vertical',
}: CanvasKeyboardNavigationOptions): void {
  // Track the last focused node for roving tabindex
  const lastFocusedNodeRef = useRef<string | null>(null);

  // Update last focused node when selection changes
  useEffect(() => {
    if (selectedNodeIds.length > 0 && selectedNodeIds[0] !== undefined) {
      lastFocusedNodeRef.current = selectedNodeIds[0];
    }
  }, [selectedNodeIds]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!enabled) return;

      // Never hijack any canvas shortcut during IME composition (Escape
      // in particular must reach the browser so it can cancel/commit
      // the in-progress composition).
      if (e.isComposing) return;

      // Allow Escape even in editable elements so users can dismiss
      // overlays/selection while focused inside an input.
      if (e.key !== 'Escape' && shouldSkipShortcut(e)) {
        return;
      }

      // Get the currently selected node
      const selectedNodeId = selectedNodeIds[0];
      const sortedNodes = sortNodesByPosition(nodes, layoutOrientation);
      const currentIndex = sortedNodes.findIndex((n) => n.id === selectedNodeId);

      // Handle different keys
      switch (e.key) {
        case 'ArrowUp': {
          e.preventDefault();
          if (!selectedNodeId) {
            // No selection - select the first node
            const firstNode = sortedNodes[0];
            if (firstNode) {
              onNodeSelect(firstNode.id);
            }
            return;
          }

          if (layoutOrientation === 'vertical') {
            // Navigate to parent (source) node
            const parents = getConnectedNodes(selectedNodeId, edges, 'sources');
            const firstParent = parents[0];
            if (firstParent) {
              onNodeSelect(firstParent);
            }
          } else {
            // In horizontal layout, up/down navigate siblings
            const siblings = getSiblingNodes(selectedNodeId, nodes, edges);
            const siblingNodes = sortedNodes.filter((n) => siblings.includes(n.id));
            const currentSiblingIndex = siblingNodes.findIndex((n) => n.id === selectedNodeId);
            if (currentSiblingIndex > 0 || siblingNodes.length > 0) {
              // Find sibling above (by Y position)
              const currentY = sortedNodes[currentIndex]?.position.y ?? 0;
              const aboveSiblings = siblingNodes.filter(
                (n) => n.position.y < currentY
              );
              const lastAbove = aboveSiblings[aboveSiblings.length - 1];
              if (lastAbove) {
                onNodeSelect(lastAbove.id);
              }
            }
          }
          break;
        }

        case 'ArrowDown': {
          e.preventDefault();
          if (!selectedNodeId) {
            // No selection - select the first node
            const firstNode = sortedNodes[0];
            if (firstNode) {
              onNodeSelect(firstNode.id);
            }
            return;
          }

          if (layoutOrientation === 'vertical') {
            // Navigate to child (target) node
            const children = getConnectedNodes(selectedNodeId, edges, 'targets');
            const firstChild = children[0];
            if (firstChild) {
              onNodeSelect(firstChild);
            }
          } else {
            // In horizontal layout, up/down navigate siblings
            const siblings = getSiblingNodes(selectedNodeId, nodes, edges);
            const siblingNodes = sortedNodes.filter((n) => siblings.includes(n.id));
            const currentY = sortedNodes[currentIndex]?.position.y ?? 0;
            const belowSiblings = siblingNodes.filter(
              (n) => n.position.y > currentY
            );
            const firstBelow = belowSiblings[0];
            if (firstBelow) {
              onNodeSelect(firstBelow.id);
            }
          }
          break;
        }

        case 'ArrowLeft': {
          e.preventDefault();
          if (!selectedNodeId) {
            const firstNode = sortedNodes[0];
            if (firstNode) {
              onNodeSelect(firstNode.id);
            }
            return;
          }

          if (layoutOrientation === 'horizontal') {
            // Navigate to parent (source) node
            const parents = getConnectedNodes(selectedNodeId, edges, 'sources');
            const firstParent = parents[0];
            if (firstParent) {
              onNodeSelect(firstParent);
            }
          } else {
            // In vertical layout, left/right navigate siblings
            const siblings = getSiblingNodes(selectedNodeId, nodes, edges);
            const siblingNodes = sortedNodes.filter((n) => siblings.includes(n.id));
            const currentX = sortedNodes[currentIndex]?.position.x ?? 0;
            const leftSiblings = siblingNodes.filter(
              (n) => n.position.x < currentX
            );
            const lastLeft = leftSiblings[leftSiblings.length - 1];
            if (lastLeft) {
              onNodeSelect(lastLeft.id);
            }
          }
          break;
        }

        case 'ArrowRight': {
          e.preventDefault();
          if (!selectedNodeId) {
            const firstNode = sortedNodes[0];
            if (firstNode) {
              onNodeSelect(firstNode.id);
            }
            return;
          }

          if (layoutOrientation === 'horizontal') {
            // Navigate to child (target) node
            const children = getConnectedNodes(selectedNodeId, edges, 'targets');
            const firstChild = children[0];
            if (firstChild) {
              onNodeSelect(firstChild);
            }
          } else {
            // In vertical layout, left/right navigate siblings
            const siblings = getSiblingNodes(selectedNodeId, nodes, edges);
            const siblingNodes = sortedNodes.filter((n) => siblings.includes(n.id));
            const currentX = sortedNodes[currentIndex]?.position.x ?? 0;
            const rightSiblings = siblingNodes.filter(
              (n) => n.position.x > currentX
            );
            const firstRight = rightSiblings[0];
            if (firstRight) {
              onNodeSelect(firstRight.id);
            }
          }
          break;
        }

        case 'Tab': {
          // Sequential navigation through all nodes
          if (!selectedNodeId || currentIndex === -1) {
            // No selection - select first or last node based on shift
            if (sortedNodes.length > 0) {
              e.preventDefault();
              const targetIndex = e.shiftKey ? sortedNodes.length - 1 : 0;
              const targetNode = sortedNodes[targetIndex];
              if (targetNode) {
                onNodeSelect(targetNode.id);
              }
            }
            return;
          }

          const nextIndex = e.shiftKey ? currentIndex - 1 : currentIndex + 1;
          if (nextIndex >= 0 && nextIndex < sortedNodes.length) {
            const nextNode = sortedNodes[nextIndex];
            if (nextNode) {
              e.preventDefault();
              onNodeSelect(nextNode.id);
            }
          }
          // If out of bounds, let Tab continue to next focusable element
          break;
        }

        case 'Enter':
        case ' ': {
          // Open/edit the selected node (Space only if not in an editable context)
          if (selectedNodeId && onNodeEdit) {
            e.preventDefault();
            onNodeEdit(selectedNodeId);
          }
          break;
        }

        case 'Delete':
        case 'Backspace': {
          // Delete the selected node — destructive, do not repeat on held key.
          if (selectedNodeId && onNodeDelete) {
            e.preventDefault();
            if (e.repeat) return;
            onNodeDelete(selectedNodeId);
          }
          break;
        }

        case 'Escape': {
          // Deselect all nodes
          if (onDeselectAll) {
            e.preventDefault();
            if (e.repeat) return;
            onDeselectAll();
          }
          break;
        }

        case 'a':
        case 'A': {
          // ⌘A on Mac, Ctrl+A on Win/Linux. Idempotent but no need to re-fire on repeat.
          if (isPrimaryMod(e) && onSelectAll) {
            e.preventDefault();
            if (e.repeat) return;
            onSelectAll();
          }
          break;
        }

        case 'd':
        case 'D': {
          // ⌘D on Mac, Ctrl+D on Win/Linux. Destructive, do not repeat.
          if (isPrimaryMod(e) && onDuplicateSelected) {
            e.preventDefault();
            if (e.repeat) return;
            onDuplicateSelected();
          }
          break;
        }
      }
    },
    [
      enabled,
      nodes,
      edges,
      selectedNodeIds,
      layoutOrientation,
      onNodeSelect,
      onNodeDelete,
      onNodeEdit,
      onDeselectAll,
      onSelectAll,
      onDuplicateSelected,
    ]
  );

  // Register the global keyboard listener
  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);
}

/**
 * Get the tabIndex for a node based on roving tabindex pattern.
 * Only the focused/selected node should have tabIndex=0, others have -1.
 */
export function getNodeTabIndex(
  nodeId: string,
  selectedNodeIds: string[],
  lastFocusedNodeId: string | null
): number {
  // If this node is selected, it should be focusable
  if (selectedNodeIds.includes(nodeId)) {
    return 0;
  }
  // If no selection but this was the last focused node
  if (selectedNodeIds.length === 0 && lastFocusedNodeId === nodeId) {
    return 0;
  }
  // If no selection and no last focused, first node gets focus
  if (selectedNodeIds.length === 0 && !lastFocusedNodeId) {
    return 0; // Will need to be checked against being the first node
  }
  return -1;
}
